import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import IndustryCard from './components/IndustryCard';
import BookDemoModal from '@/pages/home/components/BookDemoModal';

const industries = [
  {
    tag: 'HVAC',
    headline: 'HVAC Growth Systems',
    description: 'Capture emergency calls, automate scheduling, and eliminate missed opportunities during peak demand. Your AI system handles every inbound call — even at 2am during a heat wave.',
    benefits: [
      { text: '24/7 call handling — never miss an emergency' },
      { text: 'Instant dispatch routing to available technicians' },
      { text: 'Seasonal demand optimization and surge management' },
    ],
    image: 'https://public.readdy.ai/ai/img_res/1f502a3b2a1974988890e7407af1f24c.jpg',
    accent: '#F97316',
  },
  {
    tag: 'Plumbing',
    headline: 'Plumbing Automation',
    description: 'Turn urgent inbound calls into booked jobs instantly with automated response and scheduling systems. When a pipe bursts at midnight, your AI is already booking the job.',
    benefits: [
      { text: 'Emergency lead capture — 100% call coverage' },
      { text: 'Fast response automation within seconds' },
      { text: 'Increased booking rate on high-urgency calls' },
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20plumber%20in%20uniform%20working%20under%20kitchen%20sink%20with%20modern%20tools%2C%20clean%20residential%20interior%2C%20bright%20natural%20lighting%2C%20sharp%20commercial%20photography%2C%20photorealistic&width=900&height=700&seq=industry-plumbing-01&orientation=landscape',
    accent: '#06B6D4',
  },
  {
    tag: 'Roofing',
    headline: 'Roofing Lead Conversion',
    description: 'Convert high-value leads faster with structured follow-up and intelligent qualification systems. Stop letting estimates go cold — your AI follows up until the job is booked.',
    benefits: [
      { text: 'Estimate follow-up automation — no more cold leads' },
      { text: 'Lead qualification workflows for high-value jobs' },
      { text: 'Full pipeline visibility from lead to close' },
    ],
    image: 'https://readdy.ai/api/search-image?query=roofing%20contractor%20team%20installing%20new%20shingles%20on%20residential%20roof%2C%20professional%20workers%20in%20safety%20gear%2C%20clear%20sky%20background%2C%20dramatic%20wide%20angle%20shot%2C%20commercial%20photography%2C%20photorealistic&width=900&height=700&seq=industry-roofing-01&orientation=landscape',
    accent: '#2563EB',
  },
  {
    tag: 'Electrical',
    headline: 'Electrical Service Optimization',
    description: 'Improve scheduling efficiency and ensure no inbound opportunity is missed. From panel upgrades to emergency calls, every job gets captured and dispatched automatically.',
    benefits: [
      { text: 'Job routing automation to available electricians' },
      { text: 'Response time acceleration from hours to seconds' },
      { text: 'Calendar optimization to maximize daily jobs' },
    ],
    image: 'https://readdy.ai/api/search-image?query=licensed%20electrician%20in%20professional%20uniform%20working%20on%20electrical%20panel%20in%20modern%20home%2C%20focused%20expression%2C%20clean%20workspace%2C%20warm%20interior%20lighting%2C%20commercial%20photography%2C%20photorealistic&width=900&height=700&seq=industry-electrical-01&orientation=landscape',
    accent: '#22C55E',
  },
  {
    tag: 'General Contractors',
    headline: 'Full-Service Trade Growth',
    description: 'Build a centralized system that connects all lead sources, scheduling, and follow-up processes. One unified platform for every trade, every job, every revenue opportunity.',
    benefits: [
      { text: 'Unified lead management across all channels' },
      { text: 'Workflow automation for complex multi-trade jobs' },
      { text: 'Scalable infrastructure that grows with your business' },
    ],
    image: 'https://readdy.ai/api/search-image?query=general%20contractor%20in%20hard%20hat%20reviewing%20blueprints%20at%20modern%20construction%20site%2C%20professional%20team%20in%20background%2C%20golden%20hour%20lighting%2C%20wide%20angle%20commercial%20photography%2C%20photorealistic&width=900&height=700&seq=industry-gc-01&orientation=landscape',
    accent: '#F97316',
  },
];

export default function IndustriesPage() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 bg-[#060A14] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=collection%20of%20home%20service%20vehicles%20parked%20in%20a%20row%20at%20sunrise%2C%20HVAC%20plumbing%20roofing%20electrical%20vans%2C%20professional%20fleet%2C%20cinematic%20wide%20angle%2C%20deep%20blue%20sky%2C%20photorealistic%20commercial%20photography&width=1600&height=700&seq=industries-hero-01&orientation=landscape"
            alt="Industries we serve"
            className="w-full h-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060A14]/60 via-[#060A14]/50 to-[#060A14]"></div>
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] bg-[#2563EB]/12 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#06B6D4] text-xs font-semibold tracking-[0.2em] uppercase mb-6 font-inter">Industries We Serve</p>
          <h1 className="text-6xl md:text-7xl font-black text-white leading-[1.0] tracking-tight mb-6 font-poppins">
            Built for Your Trade
          </h1>
          <p className="text-white/45 text-xl leading-relaxed max-w-2xl mx-auto font-inter font-light">
            PureHomeScale is purpose-built for service-based businesses where speed, responsiveness, and operational efficiency directly impact revenue.
          </p>
        </div>
      </section>

      {/* Industry Cards */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-6">
          {industries.map((industry, i) => (
            <IndustryCard key={i} {...industry} index={i} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F172A] py-24 md:py-32 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#2563EB]/12 rounded-full blur-[80px] pointer-events-none"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#F97316] text-xs font-semibold tracking-[0.2em] uppercase mb-6 font-inter">Get Started</p>
          <h2 className="text-5xl md:text-6xl font-black text-white leading-tight mb-6 font-poppins">
            Built for Your Trade.<br />Designed to Scale.
          </h2>
          <p className="text-white/40 text-lg mb-10 max-w-xl mx-auto font-inter font-light">
            See how PureHomeScale can transform your specific trade business.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="group bg-white text-[#0F172A] font-semibold text-base px-10 py-4 rounded-full hover:bg-white/90 transition-all duration-300 cursor-pointer whitespace-nowrap font-inter inline-flex items-center gap-2.5"
          >
            Book Industry Demo
            <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform duration-200"></i>
          </button>
        </div>
      </section>

      <Footer />
      <BookDemoModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
